
/** The four areas the app has route guards for. */
export const ROLE = {
  user: "user",
  admin: "admin",
  staff: "staff",
  resourcePerson: "resource-person",
};

/** Where each role lands after signing in / when it opens another area. */
export const ROLE_HOME = {
  user: "/",
  admin: "/admin/dashboard",
  staff: "/staff/dashboard",
  "resource-person": "/resource-person/dashboard",
};

/** Each area's sign-in page (the resource-person portal uses the shared one). */
export const ROLE_LOGIN = {
  user: "/login",
  admin: "/adminlogin",
  staff: "/staff/login",
  "resource-person": "/login",
};

/** Route prefixes each area owns — used to keep `?next=` inside the area. */
export const ROLE_PREFIXES = {
  user: ["/dashboard", "/content-library", "/cart", "/achievements", "/account/settings", "/payment-success"],
  admin: ["/admin/"],
  staff: ["/staff/"],
  "resource-person": ["/resource-person/"],
};

/** API role strings (and common variants) → app area. */
const ROLE_ALIASES = {
  LEARNER: ROLE.user,
  LEARNERS: ROLE.user,
  USER: ROLE.user,
  USERS: ROLE.user,
  STUDENT: ROLE.user,
  STUDENTS: ROLE.user,

  ADMIN: ROLE.admin,
  ADMINS: ROLE.admin,
  SUPERADMIN: ROLE.admin,
  SUPERADMINISTRATOR: ROLE.admin,

  STAFF: ROLE.staff,
  STAFFS: ROLE.staff,
  STAFFMEMBER: ROLE.staff,

  RESOURCEPERSON: ROLE.resourcePerson,
  RESOURCEPERSONS: ROLE.resourcePerson,
  LECTURER: ROLE.resourcePerson,
  LECTURERS: ROLE.resourcePerson,
  TUTOR: ROLE.resourcePerson,
  TUTORS: ROLE.resourcePerson,
  INSTRUCTOR: ROLE.resourcePerson,
};

/**
 * Normalise any role shape into an app area.
 * `"resource-person" | "RESOURCE_PERSON" | "ResourcePerson" | "TUTOR"` →
 * `"resource-person"`; unknown/unusable input → `null` (never invented).
 */
export function normalizeRole(raw) {
  if (raw === undefined || raw === null || raw === "") return null;
  const text = String(raw).trim();
  const upper = text.toUpperCase();
  const compact = upper.replace(/[\s_-]+/g, "");

  if (ROLE_ALIASES[upper]) return ROLE_ALIASES[upper];
  if (ROLE_ALIASES[compact]) return ROLE_ALIASES[compact];

  const lower = text.toLowerCase();
  return Object.values(ROLE).includes(lower) ? lower : null;
}

/** Role carried by a user object (`role` / `userRole` / `user_role`). */
export function roleFromUser(user) {
  if (!user || typeof user !== "object") return null;
  return normalizeRole(user.role ?? user.userRole ?? user.user_role ?? null);
}

/** Decode a JWT payload without verifying the signature (client side only). */
export function decodeTokenPayload(token) {
  if (typeof token !== "string" || token.split(".").length < 2) return null;
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** Role carried by an access/refresh token — the authoritative source. */
export function roleFromToken(token) {
  const payload = decodeTokenPayload(token);
  if (!payload) return null;
  return normalizeRole(payload.role ?? payload.userRole ?? payload.user_role ?? null);
}

/** True when `path` belongs to `role`'s area. */
export function belongsToArea(role, path) {
  const prefixes = ROLE_PREFIXES[role];
  if (!prefixes || typeof path !== "string") return false;
  const clean = path.split("?")[0].split("#")[0];
  return prefixes.some((prefix) => clean === prefix || clean.startsWith(prefix));
}

const ABSOLUTE_OR_PROTOCOL = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * Only same-origin absolute paths are accepted (`/dashboard`,
 * `/content-library/course/9?tab=x`). `//evil.com`, `https://…`, `\\host` and
 * empty values are rejected, so `?next=` can never redirect off-site.
 */
export function safeNext(raw, fallback = null) {
  if (typeof raw !== "string") return fallback;
  const value = raw.trim();
  if (!value.startsWith("/") || ABSOLUTE_OR_PROTOCOL.test(value) || value.startsWith("\\")) {
    return fallback;
  }
  return value;
}

/**
 * The login URL for an area, carrying `?next=` back to where the user was
 * (only the shared `/login` page reads it).
 */
export function loginUrlFor(role, fromPath) {
  const loginPath = ROLE_LOGIN[role] || ROLE_LOGIN.user;
  const next = safeNext(fromPath);
  if (!next || loginPath !== ROLE_LOGIN.user) return loginPath;
  return `${loginPath}?next=${encodeURIComponent(next)}`;
}

/**
 * Navigate after a successful login: honour `next` only when it stays inside
 * the signed-in role's area, otherwise go to that role's home.
 * Replace (not push) so Back doesn't return to the login form.
 */
export function goAfterLogin(navigate, {role, next, replace = true} = {}) {
  const requested = safeNext(next);
  // Learners may return to public catalog pages after authenticating to buy.
  // Keep privileged areas isolated; other roles still return only to their own area.
  const learnerPublicNext = role === ROLE.user && requested &&
    !["/admin", "/staff", "/resource-person"].some(
      (prefix) => requested.split(/[?#]/)[0] === prefix || requested.split(/[?#]/)[0].startsWith(`${prefix}/`)
    );
  const target = requested && (belongsToArea(role, requested) || learnerPublicNext)
    ? requested
    : ROLE_HOME[role] || "/";
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.info("[auth] login redirect", {role, requested, target});
  }
  if (typeof navigate === "function") navigate(target, {replace});
  return target;
}
